import React from 'react';

import {
  ActionIcon,
  Button,
  Center,
  Divider,
  Group,
  LoadingOverlay,
  Stack,
  Text,
  Title,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { useShallowEffect } from '@mantine/hooks';
import { IconPlus } from '@tabler/icons-react';
import Drawer from 'components/Drawer';
import { RevenueCategoryAutoCompleteValue } from 'components/form-fields/auto-complete-fields/RevenueCategoryAutoComplete';
import useUpdatePackageRevenueCategories from 'hooks/package/useUpdatePackageRevenueCategories';
import usePackageRevenueCategories from 'hooks/packageRevenueCategory/usePackageRevenueCategories';
import { PackageUpdatePackageRevenueCategories } from 'types';
import notificationHandler from 'utils/notifications';
import { v4 as uuidv4 } from 'uuid';

import PackageRevenueCategoryRow from './components/PackageRevenueCategoryRow';
import RevenueCategoryManager from './components/RevenueCategoryManager';

const formId = 'package-categories-form';

interface IRevenueCategorizationDrawerProps {
  isOpen: boolean;
  closeDrawer: () => void;
  packageId: string;
  permission?: boolean;
}

export interface PackageRevenueCategoryFormValue {
  revenueCategory: RevenueCategoryAutoCompleteValue;
  revenueFraction: number;
}

export interface PackageRevenueCategoriesFormValues {
  packageRevenueCategories: Array<PackageRevenueCategoryFormValue>;
}

const PackageDrawer = ({
  isOpen,
  closeDrawer,
  packageId,
  permission = true,
}: IRevenueCategorizationDrawerProps): JSX.Element => {
  const { data, isSuccess, isLoading } = usePackageRevenueCategories({
    _package: packageId,
  });
  const updatePackageCategoriesMutation = useUpdatePackageRevenueCategories();

  const form = useForm<PackageRevenueCategoriesFormValues>({
    initialValues: {
      packageRevenueCategories: [
        {
          revenueCategory: {
            revenueCategoryId: '',
            value: '',
            label: '',
          },
          revenueFraction: 0,
        },
      ],
    },

    validate: {
      packageRevenueCategories: values => {
        return !values.length ||
          values.reduce((packageCategory, rule) => packageCategory + rule.revenueFraction, 0) === 100
          ? null
          : 'Revenue percentages must sum to 100%';
      },
    },

    transformValues: values => {
      return {
        ...values,
        packageRevenueCategories: values.packageRevenueCategories.map(packageRevenueCategory => ({
          ...packageRevenueCategory,
          revenueFraction: packageRevenueCategory.revenueFraction / 100,
        })),
      };
    },
  });

  const addPackageCategory = (): void => {
    form.insertListItem('packageRevenueCategories', {
      revenueCategory: {
        revenueCategoryId: '',
        value: '',
        label: '',
      },
      revenueFraction: 100,
    });
  };

  const handleCloseAndReset = (): void => {
    form.reset();
    closeDrawer();
  };
  const errorMessages = Object.entries(form.errors)
    .filter(error => error[0].includes(`packageRevenueCategories`))
    .map(error => error[1]);

  const handleSubmit = (values: PackageRevenueCategoriesFormValues): void => {
    const { successNotification, errorNotification } = notificationHandler();

    const payload: PackageUpdatePackageRevenueCategories = {
      packageRevenueCategories: values.packageRevenueCategories.map(packageCategory => ({
        revenueFraction: packageCategory.revenueFraction,
        revenueCategory: packageCategory.revenueCategory.revenueCategoryId,
        packageRevenueCategoryId: uuidv4(),
      })),
      packageId,
    };

    updatePackageCategoriesMutation.mutate(payload, {
      onError: error => {
        errorNotification('Package categorization failed', error.response?.data.message);
      },
      onSuccess: () => {
        successNotification(
          'Package categorization success',
          'Revenue categorization was successfully set.',
        );
        handleCloseAndReset();
      },
    });
  };

  useShallowEffect(() => {
    if (data && data.data && isSuccess) {
      const packageCategories = data.data;
      form.setValues({
        packageRevenueCategories: packageCategories.map(packageCategory => {
          return {
            revenueFraction: packageCategory.revenueFraction * 100,
            revenueCategory: {
              revenueCategoryId: packageCategory.revenueCategory?.revenueCategoryId || '',
              value: packageCategory.revenueCategory?.revenueCategoryId || '',
              label: packageCategory.revenueCategory?.name || '',
            },
          };
        }),
      });
    }
  }, [data?.data]);

  return (
    <Drawer
      opened={isOpen}
      onClose={handleCloseAndReset}
      title={<Title order={3}>Revenue Categorization</Title>}
      footer={
        <Group position='right'>
          <Button variant='subtle' onClick={handleCloseAndReset}>
            Cancel
          </Button>
          <Button
            form={formId}
            type='submit'
            loading={updatePackageCategoriesMutation.isLoading}
            disabled={!permission}
          >
            Save
          </Button>
        </Group>
      }
      size='xl'
    >
      <form id={formId} onSubmit={form.onSubmit(handleSubmit)}>
        <LoadingOverlay visible={isLoading} />
        <Text size='sm' color='dimmed' fs='italic'>
          Categorize revenue made from this package.
        </Text>
        <Stack py='md' sx={{ flexGrow: 1 }}>
          {form.values.packageRevenueCategories.map((_, index) => (
            <PackageRevenueCategoryRow key={index} permission={permission} index={index} form={form} />
          ))}
          <ActionIcon variant='subtle' color='kiloBlue' onClick={addPackageCategory} disabled={!permission}>
            <IconPlus />
          </ActionIcon>
        </Stack>
        {errorMessages.length > 0 && (
          <>
            <Stack py='sm' align='center'>
              {errorMessages.map(msg => (
                <Center key={`msg-${msg}`}>
                  <Text size='xs' c='red'>
                    {msg}
                  </Text>
                </Center>
              ))}
              <Divider />
            </Stack>
          </>
        )}
      </form>
      <Divider mx='-xl' my='lg' />
      <RevenueCategoryManager permission={permission} />
    </Drawer>
  );
};
export default PackageDrawer;
